/**
 * ConversationPanel — Slide-in Q&A panel for AI-assisted scope discovery.
 *
 * Opens from the right side of the screen when a user starts Q&A on an entity
 * (requirement, objective, etc.). Shows the full conversation history with the
 * BA persona and allows submitting answers, editing previous answers, and
 * viewing AI analysis (extracted requirements, conflicts, impact).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Send,
  MessageSquare,
  Bot,
  User,
  AlertTriangle,
  Pencil,
  Check,
  Loader2,
  Sparkles,
  Shield,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { useConversationStore } from "@/stores/conversation";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import {
  useQASession,
  useQASessions,
  useSubmitQAAnswer,
  useStartObjectiveQA,
} from "@/api/hooks";
import type { QAInteraction } from "@/api/types";
import { cn } from "@/lib/utils";

function formatTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function CoverageBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2">
      <Progress value={pct} className="h-1.5 flex-1" />
      <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{pct}%</span>
    </div>
  );
}

function DimensionBadge({ dimension }: { dimension: string | null }) {
  if (!dimension) return null;
  const colors: Record<string, string> = {
    functional: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    non_functional: "bg-purple-500/10 text-purple-600 border-purple-500/20",
    compliance: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    data: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
        colors[dimension] ?? "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
      )}
    >
      {dimension.replace("_", "-")}
    </span>
  );
}

interface MessageBubbleProps {
  interaction: QAInteraction;
  isLatest: boolean;
  onEdit?: (interactionId: string, newAnswer: string) => void;
}

function MessageBubble({ interaction, isLatest, onEdit }: MessageBubbleProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(interaction.answer ?? "");

  const handleSaveEdit = () => {
    if (editValue.trim() && onEdit) {
      onEdit(interaction.id, editValue.trim());
      setEditing(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* AI Question */}
      <div className="flex gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500/10">
          <Bot className="h-4 w-4 text-brand-600" />
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-brand-600">Scope Analyst</span>
            <DimensionBadge dimension={interaction.dimension} />
            <span className="text-[10px] text-slate-500 dark:text-slate-400">
              R{interaction.round_number}
            </span>
          </div>
          <div className="rounded-lg rounded-tl-sm bg-slate-100 dark:bg-slate-800 p-3 text-sm text-slate-900 dark:text-slate-100">
            {interaction.question}
          </div>
          {interaction.coverage_before != null && (
            <div className="max-w-[200px]">
              <CoverageBar value={interaction.coverage_before} />
            </div>
          )}
        </div>
      </div>

      {/* User Answer */}
      {interaction.answer && (
        <div className="flex gap-3">
          <div className="flex-1" />
          <div className="max-w-[85%] space-y-1">
            <div className="flex items-center justify-end gap-2">
              <span className="text-[10px] text-slate-500 dark:text-slate-400">
                {formatTime(interaction.created_at)}
              </span>
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">You</span>
            </div>
            {editing ? (
              <div className="space-y-2">
                <Textarea
                  autoResize
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="min-h-[60px] text-sm"
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveEdit}>
                    <Check className="h-3 w-3" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="group relative">
                <div className="rounded-lg rounded-tr-sm bg-brand-600 p-3 text-sm text-white">
                  {interaction.answer}
                </div>
                {onEdit && !isLatest && (
                  <button
                    onClick={() => {
                      setEditValue(interaction.answer ?? "");
                      setEditing(true);
                    }}
                    className="absolute -left-8 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-500 dark:text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 group-hover:opacity-100"
                    title="Edit this answer"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
            {interaction.coverage_after != null && (
              <div className="ml-auto max-w-[200px]">
                <CoverageBar value={interaction.coverage_after} />
              </div>
            )}
          </div>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
            <User className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          </div>
        </div>
      )}

    </div>
  );
}

export function ConversationPanel() {
  const navigate = useNavigate();
  const { isOpen, sessionId, target, isProcessing, closePanel, setSessionId, setProcessing } =
    useConversationStore();
  // Template-layer SKIN for the "Generate Documents" next-step CTA.
  const { label: skinLabel } = useTemplateLabels(target?.projectId ?? null);
  const [answer, setAnswer] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Find existing session for entity
  const { data: sessionsData } = useQASessions(
    target
      ? {
          entity_type: target.entityType,
          entity_id: target.entityId,
          limit: 1,
        }
      : undefined
  );

  // Auto-set session from existing data — accept any non-terminal status
  useEffect(() => {
    if (!sessionId && sessionsData?.items?.length) {
      const existing = sessionsData.items[0];
      if (
        existing.status === "PENDING" ||
        existing.status === "IN_PROGRESS" ||
        existing.status === "AWAITING_INPUT"
      ) {
        setSessionId(existing.id);
      }
    }
  }, [sessionsData, sessionId, setSessionId]);

  // Fetch session detail
  const { data: sessionDetail, isLoading: sessionLoading } = useQASession(sessionId ?? "");

  const submitAnswer = useSubmitQAAnswer();
  const startObjectiveQA = useStartObjectiveQA();
  const startQA = startObjectiveQA;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [sessionDetail?.interactions?.length]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!answer.trim() || !sessionId || isProcessing) return;

    const text = answer.trim();
    setSubmitError(null);
    setProcessing(true);

    try {
      await submitAnswer.mutateAsync({ sessionId, answer: text });
      // Answer was persisted — clear the input
      setAnswer("");
    } catch (err) {
      // Submission failed — restore the answer so the user can retry
      console.error("Failed to submit answer:", err);
      const message =
        err instanceof Error ? err.message : "Failed to submit answer. Please try again.";
      setSubmitError(message);
      // Don't clear the answer — keep it so the user can retry
    } finally {
      setProcessing(false);
    }
  }, [answer, sessionId, isProcessing, submitAnswer, setProcessing]);

  const handleStartQA = useCallback(async () => {
    if (!target) return;
    setProcessing(true);
    try {
      const result = await startQA.mutateAsync(target.entityId);
      // Use the returned session ID directly
      if (result?.qa_session_id) {
        setSessionId(result.qa_session_id);
      }
    } catch (err) {
      console.error("Failed to start QA:", err);
    } finally {
      setProcessing(false);
    }
  }, [target, startQA, setProcessing, setSessionId]);

  // Auto-start QA when panel opens with no existing session
  const autoStarted = useRef(false);
  useEffect(() => {
    if (
      isOpen && target && !sessionId && !isProcessing &&
      !sessionLoading && !sessionsData?.items?.length && !autoStarted.current
    ) {
      autoStarted.current = true;
      handleStartQA();
    }
    if (!isOpen) {
      autoStarted.current = false;
    }
  }, [isOpen, target, sessionId, isProcessing, sessionLoading, sessionsData, handleStartQA]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const interactions = sessionDetail?.interactions ?? [];
  const session = sessionDetail?.session;
  const pendingQuestion = interactions.find((i) => !i.answer && i.status === "ACTIVE");
  const answeredInteractions = interactions.filter((i) => i.answer);
  const isComplete = session?.status === "COMPLETED";
  const isFailed = session?.status === "FAILED";
  const coverage = session?.coverage_scores?.overall ?? 0;
  // The user answered but the AI hasn't generated the next question yet
  const isWaitingForAI = !isProcessing && !pendingQuestion && answeredInteractions.length > 0 && !isComplete && !isFailed;

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "fixed right-0 top-0 z-50 flex h-screen w-full flex-col border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl transition-transform duration-300 sm:w-[480px]",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500/10">
          <MessageSquare className="h-5 w-5 text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            Scope Discovery
          </h3>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
            {target?.entityTitle ?? "Q&A Session"}
          </p>
        </div>
        {session && (
          <Badge
            variant={
              isComplete ? "success" : session.status === "IN_PROGRESS" ? "default" : "secondary"
            }
          >
            {session.status}
          </Badge>
        )}
        <Button variant="ghost" size="icon" onClick={closePanel} aria-label="Close conversation panel">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Coverage bar + progress indicator */}
      {session && (
        <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 px-4 py-2 space-y-1.5">
          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
            <span>Coverage</span>
            <span className="font-medium">{Math.round(coverage * 100)}%</span>
          </div>
          <Progress value={Math.round(coverage * 100)} className="h-1.5" />
          <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
            <span>
              {answeredInteractions.length} question{answeredInteractions.length !== 1 ? "s" : ""} answered
            </span>
            {!isComplete && pendingQuestion && (
              <span>Round {pendingQuestion.round_number}</span>
            )}
            {isComplete && <span className="text-success font-medium">Complete</span>}
          </div>
        </div>
      )}

      {/* Messages area */}
      <ScrollArea className="flex-1">
        <div ref={scrollRef} className="space-y-4 p-4">
          {/* No session yet — starting or fallback */}
          {!sessionId && !sessionLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500/10">
                {isProcessing ? (
                  <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
                ) : (
                  <Bot className="h-8 w-8 text-brand-600" />
                )}
              </div>
              <h4 className="mb-2 text-base font-semibold text-slate-900 dark:text-slate-100">
                {isProcessing ? "Starting Scope Discovery..." : "Scope Discovery"}
              </h4>
              <p className="mb-4 max-w-[320px] text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                {isProcessing
                  ? "Preparing your first question. This may take a moment."
                  : "Have a conversation with the AI Scope Analyst. Answer questions in your own words and user stories will be extracted automatically."}
              </p>

              {!isProcessing && (
                <Button onClick={handleStartQA} disabled={isProcessing} size="lg">
                  <Sparkles className="h-4 w-4" />
                  Start Conversation
                </Button>
              )}
            </div>
          )}

          {/* Loading session data */}
          {sessionLoading && sessionId && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          )}

          {/* Waiting for first question — session exists but workflow is still generating */}
          {sessionId && !sessionLoading && session && interactions.length === 0 && !isComplete && !isFailed && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500/10">
                <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
              </div>
              <h4 className="mb-2 text-base font-semibold text-slate-900 dark:text-slate-100">
                Preparing Scope Discovery...
              </h4>
              <p className="max-w-[320px] text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                The AI Scope Analyst is analyzing your project and crafting the first question. This may take a moment.
              </p>
            </div>
          )}

          {/* Session failed — workflow could not generate first question */}
          {isFailed && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-error/10">
                <AlertTriangle className="h-8 w-8 text-error" />
              </div>
              <h4 className="mb-2 text-base font-semibold text-slate-900 dark:text-slate-100">
                Unable to Start Session
              </h4>
              <p className="mb-4 max-w-[320px] text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                The AI Scope Analyst could not generate the first question. This may be due to a temporary service issue.
              </p>
              <Button
                onClick={() => {
                  setSessionId(null);
                  autoStarted.current = false;
                }}
                size="sm"
                variant="outline"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Conversation history */}
          {interactions.map((interaction, idx) => (
            <MessageBubble
              key={interaction.id}
              interaction={interaction}
              isLatest={idx === interactions.length - 1}
            />
          ))}

          {/* Processing indicator — saving answer */}
          {isProcessing && (
            <div className="flex gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500/10">
                <Bot className="h-4 w-4 text-brand-600" />
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-800 px-4 py-3">
                <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
                <span className="text-sm text-slate-600 dark:text-slate-300">Saving your response...</span>
              </div>
            </div>
          )}

          {/* Waiting for AI to generate next question */}
          {isWaitingForAI && (
            <div className="flex gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500/10">
                <Bot className="h-4 w-4 text-brand-600" />
              </div>
              {sessionDetail?.ai_limit_message ? (
                // Row fa57fde0: the spending limit stops the next question; say so, no spinner.
                <div role="status" data-testid="qa-ai-limit-message"
                  className="rounded-lg bg-slate-100 dark:bg-slate-800 px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                  {sessionDetail.ai_limit_message}
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-800 px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Preparing next question...</span>
                </div>
              )}
            </div>
          )}

          {/* Session complete message with next steps */}
          {isComplete && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-lg border border-success/20 bg-success-bg p-3">
                <Shield className="h-5 w-5 text-success shrink-0" />
                <div>
                  <p className="text-sm font-medium text-success">Session Complete</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">
                    Coverage: {Math.round(coverage * 100)}% — {answeredInteractions.length} questions answered.
                  </p>
                </div>
              </div>

              {/* Next steps */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 p-3">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Next Steps
                </p>
                <div className="space-y-2">
                  {target?.projectId && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        navigate(`/projects/${target.projectId}/documents`);
                        closePanel();
                      }}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Generate {skinLabel("document", { plural: true, fallback: "Documents" })}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-slate-500 dark:text-slate-400"
                    onClick={closePanel}
                  >
                    <X className="h-3.5 w-3.5" />
                    Close Panel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input area */}
      {sessionId && !isComplete && !isFailed && (
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 p-4">
          {/* Error banner */}
          {submitError && (
            <div className="mb-2 flex items-start gap-2 rounded-lg bg-error/10 border border-error/20 px-3 py-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-error" />
              <div className="flex-1">
                <p className="text-xs text-error">{submitError}</p>
                <button
                  onClick={() => setSubmitError(null)}
                  className="mt-1 text-[10px] text-error/70 underline hover:text-error"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {pendingQuestion && !isProcessing && (
            <div className="mb-2 flex items-start gap-2 rounded-lg bg-brand-500/5 px-3 py-2">
              <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
              <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                {pendingQuestion.question}
              </p>
            </div>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              ref={inputRef}
              autoResize
              value={answer}
              onChange={(e) => {
                setAnswer(e.target.value);
                if (submitError) setSubmitError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                isProcessing
                  ? "Saving..."
                  : isWaitingForAI
                    ? "Waiting for next question..."
                    : pendingQuestion
                      ? "Type your answer..."
                      : "Waiting for next question..."
              }
              disabled={isProcessing || (!pendingQuestion && !answer.trim())}
              className="min-h-[44px] max-h-[160px] text-sm"
            />
            <Button
              size="icon"
              onClick={handleSubmit}
              disabled={!answer.trim() || isProcessing || !pendingQuestion}
              className="shrink-0"
              aria-label="Send answer"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-500 dark:text-slate-400">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
}
